import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Menu,
  MenuItem as MenuItemComponent,
  Button,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  NotificationsOutlined,
  Send,
  Person,
  Business,
  Add,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const MessagesPage = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [activeTab, setActiveTab] = useState(2); // Messages tab
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [openNewChatDialog, setOpenNewChatDialog] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (userInfo) {
      setUser(JSON.parse(userInfo));
    }
    
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Load all user projects
      const projectsResponse = await axios.get(`${API_URL}/projects/my-projects/`, {
        headers: { Authorization: `Token ${token}` }
      });
      
      // Load existing conversations to get unread counts
      const conversationsResponse = await axios.get(`${API_URL}/projects/conversations/`, {
        headers: { Authorization: `Token ${token}` }
      });
      
      // Create a map of project_id to conversation data
      const conversationMap = {};
      conversationsResponse.data.forEach(conv => {
        conversationMap[conv.project_id] = conv;
      });
      
      // Transform projects into conversation format
      const allConversations = projectsResponse.data.results.map(project => {
        const existingConv = conversationMap[project.id];
        return {
          project_id: project.id,
          project_name: project.name,
          last_message: existingConv?.last_message || 'No messages yet',
          last_message_time: existingConv?.last_message_time || project.created_at,
          unread_count: existingConv?.unread_count || 0,
          other_user: project.consultant_name || 'No consultant assigned',
          other_user_is_staff: true,
          has_consultant: !!project.assigned_consultant
        };
      });
      
      setConversations(allConversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (projectId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/projects/${projectId}/messages/`, {
        headers: { Authorization: `Token ${token}` }
      });
      console.log('Loaded messages:', response.data);
      const messages = response.data.results || response.data || [];
      console.log('Parsed messages:', messages);
      setMessages(messages);
      
      // Mark messages as read
      await axios.post(`${API_URL}/projects/${projectId}/messages/mark-read/`, {}, {
        headers: { Authorization: `Token ${token}` }
      });
      
      // Reload conversations to update unread count
      loadConversations();
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  };

  const loadAvailableProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/projects/my-projects/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setAvailableProjects(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleOpenNewChat = () => {
    loadAvailableProjects();
    setOpenNewChatDialog(true);
  };

  const handleStartNewChat = (project) => {
    const newConversation = {
      project_id: project.id,
      project_name: project.name,
      last_message: 'Start a conversation',
      last_message_time: new Date().toISOString(),
      unread_count: 0,
      other_user: project.consultant_name || 'No consultant assigned',
      other_user_is_staff: true,
      has_consultant: !!project.assigned_consultant
    };
    setSelectedConversation(newConversation);
    loadMessages(project.id);
    setOpenNewChatDialog(false);
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.project_id);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    // Check if consultant is assigned
    if (!selectedConversation.has_consultant) {
      alert('No consultant assigned to this project yet. Please wait for a consultant to be assigned.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_URL}/projects/${selectedConversation.project_id}/messages/`,
        { content: newMessage },
        { headers: { Authorization: `Token ${token}` } }
      );
      
      setNewMessage('');
      loadMessages(selectedConversation.project_id);
      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate('/dashboard');
    if (newValue === 1) navigate('/projects');
    if (newValue === 3) navigate('/schemes');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const capitalizeUsername = (username) => {
    if (!username) return '';
    return username.charAt(0).toUpperCase() + username.slice(1);
  };

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8f9fa', minHeight: '100vh', pb: 8 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '32px', marginRight: '12px' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              TICSPL
            </Typography>
          </Box>
          
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px'
              },
              '& .Mui-selected': {
                color: '#1976d2 !important'
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Projects" />
            <Tab label="Messages" />
            <Tab label="Schemes" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#666' }}>
              <NotificationsOutlined />
            </IconButton>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                color: '#333',
                textTransform: 'none',
                fontWeight: 500
              }}
              endIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>{user?.username?.charAt(0).toUpperCase()}</Avatar>}
            >
              {capitalizeUsername(user?.username) || 'User'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItemComponent onClick={handleMenuClose}>Profile</MenuItemComponent>
              <MenuItemComponent onClick={handleLogout}>Logout</MenuItemComponent>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
            Messages
          </Typography>
          <IconButton
            color="primary"
            onClick={handleOpenNewChat}
            sx={{
              bgcolor: '#1976d2',
              color: 'white',
              '&:hover': { bgcolor: '#1565c0' },
              width: 48,
              height: 48
            }}
          >
            <Add />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 250px)' }}>
          {/* Conversations List */}
          <Paper sx={{ width: '350px', overflow: 'auto' }}>
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Conversations
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {conversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No conversations yet
                  </Typography>
                </Box>
              ) : (
                conversations.map((conv) => (
                  <React.Fragment key={conv.project_id}>
                    <ListItem
                      button
                      selected={selectedConversation?.project_id === conv.project_id}
                      onClick={() => handleConversationClick(conv)}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: '#e3f2fd',
                          '&:hover': { bgcolor: '#bbdefb' }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: conv.other_user_is_staff ? '#ff9800' : '#1976d2' }}>
                          {conv.other_user_is_staff ? <Business /> : <Person />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {capitalizeWords(conv.project_name)}
                            </Typography>
                            {conv.unread_count > 0 && (
                              <Badge badgeContent={conv.unread_count} color="error" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ 
                              color: conv.has_consultant ? 'text.secondary' : '#f57c00',
                              fontWeight: conv.has_consultant ? 400 : 600
                            }}>
                              {conv.has_consultant ? `Consultant: ${capitalizeWords(conv.other_user)}` : '⚠️ No consultant assigned'}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ mt: 0.5, color: 'text.secondary' }}>
                              {conv.last_message}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>

          {/* Messages Area */}
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {capitalizeWords(selectedConversation.project_name)}
                  </Typography>
                  {selectedConversation.has_consultant ? (
                    <Typography variant="caption" color="text.secondary">
                      Messaging with Consultant: {capitalizeWords(selectedConversation.other_user)}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>
                      ⚠️ No consultant assigned yet - Cannot send messages
                    </Typography>
                  )}
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                  {Array.isArray(messages) && messages.length > 0 ? (
                    messages.map((message) => {
                      const isOwnMessage = message.sender === user?.id;
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                          mb: 2
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: isOwnMessage ? '#1976d2' : '#f0f0f0',
                            color: isOwnMessage ? 'white' : '#333'
                          }}
                        >
                          <Typography variant="body1">{message.content}</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              opacity: 0.8
                            }}
                          >
                            {new Date(message.created_at).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">
                        {selectedConversation.has_consultant ? 'No messages yet. Start the conversation!' : 'Waiting for consultant assignment'}
                      </Typography>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder={selectedConversation.has_consultant ? "Type your message..." : "Waiting for consultant assignment..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    size="small"
                    disabled={!selectedConversation.has_consultant}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || loading || !selectedConversation.has_consultant}
                    sx={{ minWidth: '100px' }}
                    endIcon={<Send />}
                  >
                    Send
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">
                  Select a conversation to start messaging
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* New Chat Dialog */}
      <Dialog open={openNewChatDialog} onClose={() => setOpenNewChatDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Start New Conversation</Typography>
          <IconButton onClick={() => setOpenNewChatDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a project to start messaging with your consultant
          </Typography>
          <List>
            {availableProjects.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No projects available. Create a project first to start messaging.
                </Typography>
              </Box>
            ) : (
              availableProjects.map((project) => (
                <ListItem
                  key={project.id}
                  button
                  onClick={() => handleStartNewChat(project)}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#1976d2' }}>
                      <Business />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={project.name}
                    secondary={`Consultant: ${project.consultant_name || 'Not assigned yet'}`}
                  />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewChatDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessagesPage;
